/**
 * AdvanceReceiptModal
 * -------------------
 * Standalone modal to create an Advance Receipt / Payment Acknowledgement.
 * Used from Front Desk, Billing, and (indirectly) the Patient profile.
 *
 * Scope (Phase 2A · Receipt-only):
 *   • Accepts a positive `received_amount`, `method`, optional reference
 *     and purpose note.
 *   • Requires a resolved patient (id + name).
 *   • Sends a mandatory Idempotency-Key header (regenerated on every
 *     mount so consecutive submissions cannot silently deduplicate).
 *   • Does NOT create an invoice, does NOT touch inventory / serials,
 *     and does NOT include any GST fields.
 *
 * On success it invokes `onSuccess(receipt)` and opens the printable
 * acknowledgement in a new tab.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, Receipt, ExternalLink, AlertTriangle } from 'lucide-react';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useAuth } from '../../AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const fmtINR = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const genIdempotencyKey = () => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 12);
  return `ar-ui-${ts}-${rand}`;
};

export default function AdvanceReceiptModal({
  open,
  onClose,
  patient,          // { patient_id, name, mobile, mrd } — required
  onSuccess,        // (receipt) => void
}) {
  const { clinic } = useAuth() || {};
  const idempotencyKeyRef = useRef(genIdempotencyKey());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [purposeNote, setPurposeNote] = useState('');
  // 2026-02-19 · Item-level capture (free-text) + Total Amount so the
  // printed receipt shows HA/Model/Mould/Others and a Balance line.
  const [hearingAid, setHearingAid] = useState('');
  const [model, setModel] = useState('');
  const [mould, setMould] = useState('');
  const [others, setOthers] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);   // successful receipt row

  const balance = useMemo(() => {
    const t = Number(totalAmount);
    const a = Number(amount);
    if (!Number.isFinite(t) || !Number.isFinite(a)) return null;
    if (t <= 0 || a < 0) return null;
    return Math.max(0, Math.round((t - a) * 100) / 100);
  }, [totalAmount, amount]);

  const canSubmit = useMemo(() => {
    const amt = Number(amount);
    const tot = Number(totalAmount);
    const hasPatient = !!(patient?.patient_id);
    const amtOk = Number.isFinite(amt) && amt > 0;
    // Total Amount is required by the form (per 2026-02-19 spec) and
    // MUST be >= Advance Collected so Balance is never negative.
    const totOk = Number.isFinite(tot) && tot > 0 && tot >= amt;
    return hasPatient && amtOk && totOk && method;
  }, [amount, method, patient, totalAmount]);

  const reset = useCallback(() => {
    idempotencyKeyRef.current = genIdempotencyKey();
    setAmount(''); setMethod('cash'); setReference('');
    setPurposeNote(''); setError(null); setCreated(null);
    setHearingAid(''); setModel(''); setMould(''); setOthers('');
    setTotalAmount('');
    setSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose?.();
  }, [onClose, reset]);

  const openPrintable = useCallback((receipt) => {
    if (!receipt?.receipt_id) return;
    const url = `${API}/advance-receipts/${receipt.receipt_id}/receipt.pdf`;
    // Include auth via bearer token propagation is handled by axios interceptor;
    // for direct browser open we rely on cookie auth (set at login).
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  // 2026-02-19 · Email the receipt PDF to the patient (Resend-backed).
  // Flow:
  //   • If patient.email exists → one-click send.
  //   • Else prompt the operator for a one-off email address.
  // Backend returns { ok: true, to, status: "queued" }.
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const sendEmail = useCallback(async (receipt) => {
    if (!receipt?.receipt_id || emailBusy) return;
    let to = patient?.email?.trim() || null;
    if (!to) {
      // Soft-prompt for a one-off address when no email on file.
      // eslint-disable-next-line no-alert
      to = window.prompt('No email on file for this patient. Enter an address to send to:');
      if (!to) return;
    }
    setEmailBusy(true); setEmailResult(null);
    try {
      const r = await axios.post(
        `${API}/advance-receipts/${receipt.receipt_id}/email`,
        null,
        { params: { to } },
      );
      setEmailResult({ ok: true, to: r.data?.to || to });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Email failed';
      setEmailResult({ ok: false, error: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally {
      setEmailBusy(false);
    }
  }, [emailBusy, patient]);

  const submit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true); setError(null);
    try {
      const body = {
        patient_id: patient.patient_id,
        received_amount: Number(amount),
        method,
        reference: reference.trim() || null,
        purpose_note: purposeNote.trim() || null,
        hearing_aid: hearingAid.trim() || null,
        model: model.trim() || null,
        mould: mould.trim() || null,
        others: others.trim() || null,
        total_amount: Number(totalAmount),
      };
      const r = await axios.post(`${API}/advance-receipts`, body, {
        headers: { 'Idempotency-Key': idempotencyKeyRef.current },
      });
      setCreated(r.data);
      onSuccess?.(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((x) => x.msg || String(x)).join(', ')
        : (typeof detail === 'string' ? detail : (e?.message || 'Could not create receipt'));
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [amount, method, reference, purposeNote, patient, canSubmit, submitting, onSuccess,
      hearingAid, model, mould, others, totalAmount]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="advance-receipt-modal"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-sky-50 to-cyan-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-sky-600 text-white flex items-center justify-center shadow-sm">
              <Receipt size={18} />
            </div>
            <div>
              <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">Advance Receipt</h3>
              <p className="text-[11px] text-slate-500">Payment Acknowledgement · Not a Tax Invoice</p>
            </div>
          </div>
          <button
            className="text-slate-500 hover:text-slate-800 p-1 rounded-full hover:bg-white transition"
            data-testid="advance-receipt-close-btn"
            onClick={handleClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {!created ? (
          <div className="px-6 py-4 space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[12px] text-slate-700" data-testid="advance-receipt-patient-summary">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Patient</div>
              <div className="font-semibold text-slate-900">
                {patient?.name || '—'}
                {patient?.mrd && <span className="ml-2 text-slate-500 font-normal">· MRD {patient.mrd}</span>}
                {patient?.mobile && <span className="ml-2 text-slate-500 font-normal">· {patient.mobile}</span>}
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-[12px] text-rose-800" data-testid="advance-receipt-error">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Advance Collected (₹) *
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="advance-receipt-amount-input"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                autoFocus
              />
            </div>

            {/* 2026-02-19 · Item-level capture — all optional. These
                print on the receipt (Hearing Aid / Model / Mould / Others). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Hearing Aid (brand)
                </label>
                <input
                  type="text"
                  value={hearingAid}
                  onChange={(e) => setHearingAid(e.target.value)}
                  placeholder="e.g. Signia"
                  data-testid="advance-receipt-hearing-aid-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. Motion C&G 200 RIC B/L"
                  data-testid="advance-receipt-model-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Mould
                </label>
                <input
                  type="text"
                  value={mould}
                  onChange={(e) => setMould(e.target.value)}
                  placeholder="e.g. Soft RCC Mould B/L"
                  data-testid="advance-receipt-mould-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Others
                </label>
                <input
                  type="text"
                  value={others}
                  onChange={(e) => setOthers(e.target.value)}
                  placeholder="Batteries, accessories…"
                  data-testid="advance-receipt-others-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
            </div>

            {/* Total Amount + computed Balance */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Total Amount (₹) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="Full quoted price"
                  data-testid="advance-receipt-total-amount-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
                {Number(totalAmount) > 0 && Number(amount) > Number(totalAmount) && (
                  <div className="text-[10px] text-rose-600 mt-1" data-testid="advance-receipt-balance-warn">Total must be ≥ Advance Collected</div>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Balance (auto)
                </label>
                <div
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-800 font-bold tabular-nums"
                  data-testid="advance-receipt-balance-display"
                >
                  {balance === null ? '—' : fmtINR(balance)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Method *
                </label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  data-testid="advance-receipt-method-select"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none bg-white"
                >
                  {METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Reference # (optional)
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="UPI txn, cheque #"
                  data-testid="advance-receipt-reference-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Purpose Note (optional)
              </label>
              <textarea
                value={purposeNote}
                onChange={(e) => setPurposeNote(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g., Advance for hearing-aid trial"
                data-testid="advance-receipt-purpose-input"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none"
              />
              <div className="text-[10px] text-slate-400 mt-1 italic">
                This note is informational only. The money is not bound to any product.
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-[11px] text-amber-900 leading-relaxed">
              <strong>Important:</strong> This receipt acknowledges cash-in-hand only.
              It is NOT a Tax Invoice, does NOT recognise revenue, does NOT include GST,
              and does NOT reserve inventory. A Tax Invoice will be issued separately
              when the product / service is finalised.
            </div>
          </div>
        ) : (
          <div className="px-6 py-6 text-center space-y-4" data-testid="advance-receipt-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
              <Receipt size={26} />
            </div>
            <div>
              <h4 className="text-base font-bold text-slate-900">Receipt Created</h4>
              <p className="text-[13px] text-slate-500 mt-1">
                <span className="font-mono">{created.receipt_no}</span>
                {' · '}
                <span className="font-semibold text-slate-900">{fmtINR(created.received_amount)}</span>
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <button
                onClick={() => openPrintable(created)}
                data-testid="advance-receipt-print-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700 transition shadow-sm"
              >
                <ExternalLink size={14} /> Open Printable Receipt
              </button>
              {/* 2026-02-19 · Email the PDF receipt via Resend */}
              <button
                onClick={() => sendEmail(created)}
                disabled={emailBusy}
                data-testid="advance-receipt-email-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition shadow-sm disabled:opacity-60"
                title={patient?.email ? `Send PDF to ${patient.email}` : 'Enter a one-off email address when prompted'}
              >
                <Receipt size={14} /> {emailBusy ? 'Sending…' : 'Email PDF to patient'}
              </button>
              {/* 2026-02-19 · wa.me click-to-chat (no API cost) */}
              <WhatsAppButton
                mobile={patient?.mobile}
                size="md"
                testid="advance-receipt-whatsapp-btn"
                message={(() => {
                  const rec = created || {};
                  const name = patient?.name || rec.patient_name || 'Patient';
                  const amt = Number(rec.received_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  const ha = [rec.hearing_aid, rec.model].filter(Boolean).join(' ') || 'your order';
                  const balance = (rec.total_amount != null)
                    ? Number((rec.total_amount || 0) - (rec.received_amount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : null;
                  const bLine = balance != null ? ` Balance due: \u20B9${balance}.` : '';
                  const cName = clinic?.name || 'AUDINEXA Clinic';
                  return `Dear ${name}, please find your advance receipt for \u20B9${amt} towards ${ha}. Receipt No: ${rec.receipt_no || ''}.${bLine} — ${cName}`;
                })()}
              />
            </div>
            {emailResult && (
              <div
                className={`mt-3 text-xs text-center ${emailResult.ok ? 'text-emerald-700' : 'text-rose-700'}`}
                data-testid="advance-receipt-email-result"
              >
                {emailResult.ok
                  ? `Queued · PDF will arrive at ${emailResult.to}`
                  : `Could not send: ${emailResult.error}`}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          {!created ? (
            <>
              <button
                onClick={handleClose}
                data-testid="advance-receipt-cancel-btn"
                className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                data-testid="advance-receipt-submit-btn"
                className="px-4 py-2 bg-sky-600 text-white text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 hover:bg-sky-700 transition"
              >
                {submitting ? 'Saving…' : `Receive ${amount ? fmtINR(amount) : 'Advance'}`}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              data-testid="advance-receipt-done-btn"
              className="px-4 py-2 bg-slate-700 text-white text-sm font-semibold rounded-lg hover:bg-slate-900 transition"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
